import React, {
  createContext,
  useReducer,
  useContext,
  useEffect,
  useCallback,
  memo
} from 'react';
import authReducer from '../reducers/AuthReducer';
import setAuthToken from '../components/utils/setAuthToken';
import axios from 'axios';
import { http } from '../features/jobs/api/http';
import { API } from '../config/api';

const AuthContext = createContext();

const AUTH_API = `${API.BACKEND}/api/v1/users`;

const AuthProvider = ({ children }) => {
  const initialState = {
    token: localStorage.getItem('token'),
    isAuthenticated: false,
    loading: true,
    user: null,
    error: null
  };

  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user from token on mount
  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      dispatch({ type: 'AUTH_ERROR' });
      return;
    }

    setAuthToken(token);

    try {
      const res = await axios.get(`${AUTH_API}/auth`);
      dispatch({
        type: 'USER_LOADED',
        payload: res.data.data
      });
    } catch (err) {
      console.error('Auth load failed:', err?.response?.status);
      setAuthToken(null);
      dispatch({ type: 'AUTH_ERROR' });
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      await axios.post(`${AUTH_API}/logout`);
    } catch {}
    setAuthToken(null);
    dispatch({ type: 'LOGOUT' });
  }, []);

  // Load user on first render
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Keep token header in sync
  useEffect(() => {
    setAuthToken(state.token);
  }, [state.token]);

  // Keep the x-actor header (activity "by whom") in sync with the logged-in user, so
  // every :5100 write (fork / reconcile / edit …) is attributed without per-call edits.
  useEffect(() => {
    const u = state.user;
    const name = u?.name || u?.email || u?.username || '';
    if (name) http.defaults.headers.common['x-actor'] = encodeURIComponent(name);
    else delete http.defaults.headers.common['x-actor'];
  }, [state.user]);

  return (
    <AuthContext.Provider value={{ state, dispatch, logout, loadUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const ctx = useContext(AuthContext);
  return [ctx.state, ctx.dispatch, { logout: ctx.logout, loadUser: ctx.loadUser }];
};

export default memo(AuthProvider);
